import moment, {Moment} from 'moment';
import {
  Author,
  Benefit,
  ChapterItem,
  ContentCounters,
  CourseGiftItem,
  CourseGifts,
  CoursePromoPreview,
  DateFormat,
  Dates,
  ICoursePromoContext,
  Prices,
  Questions,
  TargetAudience,
  TeacherItem,
} from './types';
import {CourseObject, TeacherObject,} from 'api/course';
import {getFilePath} from 'api/library';
import {Currency} from 'types';

const empty = '';

/**
 * The function return course promo data
 * @param {CourseObject} course
 * @return {ICoursePromoContext}
 */
export function getPromoData(course: CourseObject): ICoursePromoContext {
  return {
    title: course.title,
    description: course.description,
    preview: getPreview(course),
    prices: getPrices(course),
    chapters: getChapters(course),
    contentCounters: getContentCounters(course),
    targetAudience: getTargetAudience(course),
    benefit: getBenefit(course),
    author: getAuthor(course),
    teachers: getTeachers(course),
    questions: getFaq(course),
    daysAccessCount: getDaysAccess(course),
    dates: getDates(course),
  };
}

/**
 * The function return course promo preview data
 * @param {CourseObject} course
 * @return {CoursePromoPreview}
 */
export function getPreview(course: CourseObject): CoursePromoPreview {
  const cover = course?.preview?.id
      ? getFilePath(course.preview.id)
      : empty;

  const video = course?.video?.preview?.id
      ? getFilePath(course.video.preview.id)
      : empty;

  return {
    cover,
    video,
  };
}

/**
 * The function return course promo gifts data
 * @param {CourseObject[]} courses
 * @return {CourseGifts}
 */
export function getGiftCourses(
    courses: CourseObject[],
): CourseGifts {
  const items: CourseGiftItem[] = [];

  if (courses.length) {
    courses
        .forEach((freeCourse: CourseObject) => {
          items.push({
            id: freeCourse?.id || empty,
            title: freeCourse?.title || empty,
            cover: freeCourse?.preview && getFilePath(freeCourse.preview?.id)
              || empty,
            link: 'course link',
          });
        });
  }

  return {
    title: 'Курсы в подарок!',
    items,
  };
}

/**
 * The function return course promo prices data
 * @param {CourseObject} course
 * @return {Prices}
 */
export function getPrices(course: CourseObject): Prices {
  const {price, sale} = course;

  const result: Prices = {
    current: price.amount || 0,
    currency: price.currency || Currency.RUB,
  };

  if (sale?.dates) {
    const {startDate, overDate} = sale.dates;
    const isNowOnSale = sale.active && checkSalePeriod(startDate, overDate);

    if (isNowOnSale&& sale?.limits) {
      result.old = price.amount;
      result.current = sale.price;
      result.isInstallmentAvailable = true;
    }
  }

  if (sale?.limits) {
    result.discountSeats = sale.limits;
  }

  if (sale?.dates?.overDate) {
    result.discountEndDate
        = moment(sale.dates.overDate);
  }

  return result;
}

/**
 * The function return course promo chapters data
 * @param {CourseObject} course
 * @return {ChapterItem[]}
 */
export function getChapters(course: CourseObject): ChapterItem[] {
  const result: ChapterItem[] = [];

  if (course?.programs?.content) {
    if ('chapters' in course
        ?.programs
        ?.content
    ) {
      course
          .programs
          .content
          .chapters
          .forEach(({title}) => {
            if (title) {
              result.push(title);
            }
          });
    }
  }

  return result;
}

/**
 * The function return course promo content counters
 * @param {CourseObject} course
 * @return {ContentCounters}
 */
export function getContentCounters(course: CourseObject): ContentCounters {
  return {
    lessons: course.counters?.lesson || 0,
    tasks: course.counters?.read || 0,
    webinars: course.counters?.webinar || 0,
  };
}

/**
 * The function return course promo target audience data
 * @param {CourseObject} course
 * @return {TargetAudience}
 */
export function getTargetAudience(course: CourseObject): TargetAudience {
  const items = course
      .audience
      ?.list
      .map((item) => ({
        title: item.title,
        description: item.description,
        preview: item?.preview?.id,
      }));

  return {
    title: course.audience?.title || empty,
    items: items || [],
  };
}

/**
 * The function return course promo benefit data
 * @param {CourseObject} course
 * @return {Benefit}
 */
export function getBenefit(course: CourseObject): Benefit {
  const active = course.benefit?.active;

  const items = course
      .benefit
      ?.list
      .map((item) => item.description);

  return {
    title: course.benefit?.title || empty,
    items: active && items || [],
  };
}

/**
 * The function return course promo author data
 * @param {CourseObject} course
 * @return {Author}
 */
export function getAuthor(course: CourseObject): Author {
  const author = course.authorMessage?.teacher;
  const video = course.videoMessage?.active && course.videoMessage?.preview?.id;
  const preview = author?.preview?.id && getFilePath(author.preview.id);

  return {
    name: author?.name || empty,
    message: author?.text || empty,
    preview: preview || empty,
    video: video || empty,
    role: author?.role || empty,
  };
}

/**
 * The function return course promo teachers
 * @param {CourseObject} course
 * @return {Author}
 */
export function getTeachers(course: CourseObject): TeacherItem[] {
  const items = course
      .teachers
      ?.list
      .map((teacher: TeacherObject) => {
        const imgSrc = teacher.preview?.id && getFilePath(teacher.preview.id);
        return {
          name: teacher.name,
          role: teacher.role,
          imgSrc: imgSrc || empty,
          text: teacher.text,
        };
      });

  return items || [];
}

/**
 * The function return course promo FAQ data
 * @param {CourseObject} course
 * @return {Questions}
 */
export function getFaq({faq}: CourseObject): Questions {
  if (faq) {
    const {active, list, title} = faq;

    const questions = active && list || [];

    return {
      title: title || empty,
      items: questions,
    };
  }

  return {
    title: empty,
    items: [],
  };
}

/**
 * The function return course promo FAQ data
 * @param {CourseObject} course
 * @return {number}
 */
export function getDaysAccess(course: CourseObject): number {
  const active = course.postAccess?.active;
  const days = course?.postAccess?.days;

  return (active && days) ? days : 0;
}

/**
 * The function return course dates data
 * @param {CourseObject} course
 * @return {Questions}
 */
export function getDates(course: CourseObject): Dates {
  const fixedActive = course.fixedDate?.active;
  const fixedStart = fixedActive
    && course
        .fixedDate
        ?.dates
        .startDate
  ;
  const fixedEnd
    = course.fixedDate?.dates?.overDate
    || course.salesDates?.overDate;

  const start = fixedStart
    ? fixedStart
    : course.salesDates?.startDate;

  const result:Dates = {
    active: Boolean(fixedActive),
    start: moment(start),
    end: moment(fixedEnd),
  };

  if (start && fixedEnd) {
    const diff = moment(start).diff(fixedEnd) * -1;
    result.duration = getDaysCountFromMs(diff, true);
  }

  return result;
}

/**
 * The function return discount percent
 * @param {number} oldPrice
 * @param {number} newPrice
 * @return {number}
 */
export function getDiscountPercent(oldPrice: number, newPrice: number) {
  const percent = 100 - (newPrice * 100 / oldPrice);
  return Math.floor(percent);
}

/**
 * The function return true if current date is in the sale period
 * @param {Moment} startDate
 * @param {Moment} overDate
 * @return {Boolean}
 */
export function checkSalePeriod(
    startDate: Moment | string,
    overDate: Moment | string,
): Boolean {
  const now = moment();
  const isStarted = now.diff(startDate) > 0;
  const isNotOver = now.diff(overDate) < 0;

  return isStarted && isNotOver;
}

/**
 * The function return formatted date
 * @param {Moment} date
 * @param {DateFormat} format
 * @return {string | null}
 */
export function getFormattedDate(
    date: Moment | undefined,
    format: DateFormat,
): string | null {
  return date?.format(format) || null;
}

/**
 * The function return days count from milliseconds
 * @param {number} milliseconds
 * @param {boolean} floor
 * @return {number}
 */
export function getDaysCountFromMs(milliseconds: number, floor?: boolean) {
  const days = milliseconds / 1000 / 60 / 60 / 24;
  return floor ? Math.floor(days) : days;
}
